import { useState } from 'react';
import { useGmbAsIs } from '../hooks/useGmbStrategy';
import type { AsIsKPI, AsIsProductService, AsIsProfileBreakdown, CompetitionRow, KeywordPositionData } from '../types';

interface AsIsStateProps {
  onTooltipShow: (title: string, content: string) => void;
}

function formatPeriodDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '?';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '?';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function buildComparisonLabel(asIsData: any): string | null {
  const periods = asIsData?.kpi_periods;
  if (!periods?.current) return null;
  const cur = periods.current;
  if (!cur?.start || !cur?.end) return null;
  const label = `Comparison: ${formatPeriodDate(cur.start)} – ${formatPeriodDate(cur.end)}`;
  if (periods.previous?.start && periods.previous?.end) {
    return `${label} vs ${formatPeriodDate(periods.previous.start)} – ${formatPeriodDate(periods.previous.end)}`;
  }
  return label;
}

function formatCalculatedDate(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) +
    `, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

export default function AsIsState({ onTooltipShow }: AsIsStateProps) {
  const { data: asIsData, isLoading, error, refetch } = useGmbAsIs();
  const [showAllOthers, setShowAllOthers] = useState(false);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <i className="ri-error-warning-line text-red-500 text-3xl mb-3 block"></i>
        <h3 className="text-sm font-semibold text-red-800">Failed to load As-Is data</h3>
        <p className="text-xs text-red-600 mt-1">{error.message}</p>
        <button onClick={() => refetch()} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 animate-pulse">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16 mb-3"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const data = asIsData?.data || {};
  const profileBreakdown: AsIsProfileBreakdown[] = data.profile_breakdown || [];
  const gmbKPIs: AsIsKPI[] = data.kpis || [];
  const competitionData = data.competition || { headers: [] as string[], rows: [] as CompetitionRow[], competitors: [] };
  const competitorDetails = competitionData.competitors || [];
  const compKeys: string[] = competitionData.compKeys
    || competitorDetails.map((_: unknown, i: number) => `comp${String.fromCharCode(65 + i)}`);
  const keywordPosition = data.keyword_position as KeywordPositionData | null | undefined;
  const productsServices: AsIsProductService[] = data.products_services || [];
  const productsWithDescription = productsServices.filter((ps) => !!ps.description);
  const productsWithoutDescription = productsServices.filter((ps) => !ps.description);

  const getBarColor = (value: number) => {
    if (value >= 70) return 'bg-emerald-500';
    if (value >= 40) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getTrendIcon = (trend: string, hasChange: boolean) => {
    if (!hasChange) return 'ri-subtract-line text-gray-400';
    switch (trend) {
      case 'up': return 'ri-arrow-up-line text-emerald-500';
      case 'down': return 'ri-arrow-down-line text-red-500';
      default: return 'ri-subtract-line text-gray-400';
    }
  };

  const getTrendPillClass = (kpi: AsIsKPI) => {
    if (kpi.change === '--') return 'bg-gray-100 text-gray-600';
    return kpi.trend === 'up' ? 'bg-emerald-100 text-emerald-700' : kpi.trend === 'down' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600';
  };

  const getPillClass = (pill: string) => {
    switch (pill) {
      case 'better': return 'bg-emerald-100 text-emerald-700';
      case 'worse': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const categoryDisplayLabels: Record<string, string> = {
    Products: 'Products/Services',
    'Products & Services': 'Products/Services',
  };

  return (
    <div className="space-y-6">
      {/* Row 1 — Top 3 Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          {
            label: 'Keyword Position',
            value: keywordPosition?.avg_position != null ? `#${keywordPosition.avg_position}` : '--',
            icon: 'ri-key-2-line',
            small: false,
            description: keywordPosition
              ? `${keywordPosition.ranked_count} of ${keywordPosition.tracked_count} keywords ranked`
              : 'Connect GSC to fetch keyword rankings',
          },
          { label: 'Profile Health Score', value: data.profile_health_score ? `${data.profile_health_score}/100` : '—', icon: 'ri-profile-line', small: false },
          { label: 'Reviews', value: data.total_reviews?.toString() || '—', icon: 'ri-star-line', small: false },
        ].map((stat) => (
          <div
            key={stat.label}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-sm transition-shadow"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{stat.label}</span>
              <div className="w-8 h-8 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <i className={`${stat.icon} text-gray-500 dark:text-gray-400 text-sm`}></i>
              </div>
            </div>
            <div className={`font-bold text-gray-900 dark:text-white ${stat.small ? 'text-sm text-gray-400 dark:text-gray-500 leading-snug' : 'text-2xl'}`}>{stat.value}</div>
            {stat.description && (
              <div className="text-xs text-gray-500 mt-1">{stat.description}</div>
            )}
          </div>
        ))}
      </div>

      {/* Profile Optimization Breakdown */}
      {profileBreakdown.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Profile Health Score Breakdown</h3>
            <button
              onClick={() => onTooltipShow('Profile Health Score', 'This chart shows how complete and optimized each section of your Google Business Profile is. Green = well optimized, orange = needs work, red = critical gaps.')}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="More info"
            >
              <i className="ri-information-line text-lg"></i>
            </button>
          </div>
          <div className="space-y-3">
            {profileBreakdown.map((item) => (
              <div key={item.label} className="flex items-center gap-3">
                <span className="text-sm text-gray-600 dark:text-gray-400 w-32 flex-shrink-0">
                  {categoryDisplayLabels[item.label] ?? item.label}
                </span>
                <div className="flex-1 h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${getBarColor(item.value)} transition-all duration-500`} style={{ width: `${item.value}%` }}></div>
                </div>
                <span className={`text-sm font-semibold w-10 text-right ${item.value >= 70 ? 'text-emerald-600' : item.value >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                  {item.value}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Products & Services */}
      {productsServices.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line text-teal-600 dark:text-teal-400 text-sm"></i>
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Products &amp; Services</h3>
            </div>
            <button
              onClick={() => onTooltipShow('Products & Services', 'Everything listed on your Google Business Profile. Items with a description are shown first — BRD recommends at least 3 products or services with descriptions for a complete profile.')}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="More info"
            >
              <i className="ri-information-line text-lg"></i>
            </button>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-5">
            {productsServices.length} listed on your Google Business Profile
            {productsWithDescription.length > 0 && ` · ${productsWithDescription.length} with a description`}
          </p>

          {productsWithDescription.length > 0 && (
            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {productsWithDescription.map((ps, idx) => (
                <div
                  key={`${ps.name}-${idx}`}
                  className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-3.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{ps.name}</span>
                    {ps.price && (
                      <span className="text-xs font-medium text-teal-600 dark:text-teal-400 flex-shrink-0">{ps.price}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{ps.description}</p>
                </div>
              ))}
            </div>
          )}

          {productsWithoutDescription.length > 0 && (
            <div className={productsWithDescription.length > 0 ? 'mt-5 pt-4 border-t border-gray-100 dark:border-gray-700' : ''}>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2.5">
                Also listed <span className="font-normal text-gray-400 dark:text-gray-500">(no description on GBP)</span>
              </p>
              <div className="flex flex-wrap gap-1.5">
                {(showAllOthers ? productsWithoutDescription : productsWithoutDescription.slice(0, 24)).map((ps, idx) => (
                  <span
                    key={`${ps.name}-${idx}`}
                    className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                  >
                    {ps.name}
                  </span>
                ))}
                {!showAllOthers && productsWithoutDescription.length > 24 && (
                  <button
                    onClick={() => setShowAllOthers(true)}
                    className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 font-medium hover:bg-teal-100 dark:hover:bg-teal-900/50 transition-colors"
                  >
                    +{productsWithoutDescription.length - 24} more
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}


      {/* GMB KPIs */}
      {gmbKPIs.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">GMB KPIs</h3>
            <button
              onClick={() => onTooltipShow('GMB KPIs', 'Key performance indicators pulled directly from your Google Business Profile Insights. These track how customers interact with your profile. Comparison shows the latest 28-day window vs the previous 28-day window. Reviews and Rating compare the current vs previous calendar month.')}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="More info"
            >
              <i className="ri-information-line text-lg"></i>
            </button>
          </div>
          {buildComparisonLabel(data) && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-5">{buildComparisonLabel(data)}</p>
          )}
          {formatCalculatedDate(data.calculated_at) && (
            <p className={`text-[11px] text-gray-400 dark:text-gray-500 mb-5 ${buildComparisonLabel(data) ? '-mt-3' : 'mt-1'}`}>
              Data calculated on {formatCalculatedDate(data.calculated_at)}
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {gmbKPIs.map((kpi) => (
              <div key={kpi.label} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{kpi.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold text-gray-900 dark:text-white">{kpi.value}</span>
                  {kpi.previous && (
                    <span className="text-xs font-medium text-gray-400">← {kpi.previous}</span>
                  )}
                  <span className={`inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded ${getTrendPillClass(kpi)}`}>
                    <i className={getTrendIcon(kpi.trend, kpi.change !== '--')}></i>
                    {kpi.change}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Competition Comparison */}
      {competitorDetails.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Competition Comparison</h3>
            <button
              onClick={() => onTooltipShow('Competition Comparison', 'Compare your GMB profile metrics against your top local competitors. Better = you lead, Worse = they lead, Similar = within 10%.')}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="More info"
            >
              <i className="ri-information-line text-lg"></i>
            </button>
          </div>
          <div className="overflow-x-auto">
            {competitorDetails.some((competitor) => competitor.competitor_url || competitor.competitor_insights) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                {competitorDetails.map((competitor) => {
                  const website = competitor.competitor_url || '';
                  const safeWebsite = /^https?:\/\//i.test(website) ? website : null;
                  return (
                    <div key={competitor.label} className="rounded-lg border border-gray-100 dark:border-gray-700 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{competitor.label}</span>
                        <span className="text-[10px] uppercase tracking-wide text-gray-400">
                          {competitor.source === 'onboarding' ? 'Provided during onboarding' : 'Search result'}
                        </span>
                      </div>
                      {safeWebsite && (
                        <a
                          href={safeWebsite}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 block text-xs text-teal-700 dark:text-teal-400 hover:underline"
                        >
                          {website}
                        </a>
                      )}
                      {competitor.competitor_insights && (
                        <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">{competitor.competitor_insights}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  {competitionData.headers.map((h: string, idx: number) => (
                    <th
                      key={h}
                      className={`text-left text-xs font-semibold uppercase tracking-wide py-3 px-4 ${
                        idx === 1
                          ? 'text-violet-600 dark:text-violet-400 bg-violet-50/50 dark:bg-violet-900/10'
                          : idx === 0
                            ? 'text-gray-500 dark:text-gray-400'
                            : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      {idx === 1 && <i className="ri-user-star-line mr-1"></i>}
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {competitionData.rows.map((row, idx) => (
                  <tr key={row.metric || idx} className="border-b border-gray-100 dark:border-gray-700/50 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="py-3.5 px-4 text-sm font-medium text-gray-900 dark:text-white">
                      {row.metric}
                    </td>
                    <td className="py-3.5 px-4 bg-violet-50/30 dark:bg-violet-900/5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{row.yours?.value}</span>
                        {row.yours?.pill && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                            row.yours.pill === 'better'
                              ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                              : row.yours.pill === 'worse'
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}>
                            {row.yours.pill === 'better' && <i className="ri-arrow-up-s-line mr-0.5"></i>}
                            {row.yours.pill === 'worse' && <i className="ri-arrow-down-s-line mr-0.5"></i>}
                            {row.yours.pill}
                          </span>
                        )}
                      </div>
                    </td>
                    {compKeys.map((compKey) => {
                      const cell = row[compKey as `comp${string}`];
                      return (
                        <td key={compKey} className="py-3.5 px-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400">{cell?.value}</span>
                            {cell?.pill && (
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                cell.pill === 'better'
                                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                                  : cell.pill === 'worse'
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                              }`}>
                                {cell.pill}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700/50">
            <p className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1.5">
              <i className="ri-information-line"></i>
              Post data unavailable for competitors — this metric is only available for your own profile.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Competition Comparison</h3>
            <button
              onClick={() => onTooltipShow('Competition Comparison', 'Compare your GMB profile metrics against your top local competitors.')}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="More info"
            >
              <i className="ri-information-line text-lg"></i>
            </button>
          </div>
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-map-pin-user-line text-blue-500 dark:text-blue-400 text-xl"></i>
            </div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Low competition zone</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">No local competitors found in your area. Your profile stands out.</p>
          </div>
        </div>
      )}
    </div>
  );
}
